import React, { ChangeEvent, useId } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { isFirefox } from 'flow/utils/browser-utils';
import { MeetingLocation } from 'flow/utils/meeting-constants';
import {
    changeMicDevice,
    changeVideoDevice,
} from 'flow/actions/meeting-actions';
import {
    selectMicInUse,
    selectAudioOutputInUse,
    selectVideoInUse,
    selectMicDeviceList,
    selectAudioOutputDeviceList,
    selectVideoDeviceList,
    selectLobbyMicDeviceInUse,
    selectLobbyVideoDeviceInUse,
    selectLobbyAudioDeviceInUse,
} from 'flow/selectors/meeting-selectors';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';

import { ConnectedMediaDevice } from 'flow/types/Headset';

interface MediaDevice extends ConnectedMediaDevice {}

// Browsers enumerate the OS default device twice: once as a synthetic entry
// with deviceId 'default' (and 'communications' on Windows) and once as the
// underlying physical device. Both share a groupId. Collapse them so the user
// only sees the default entry, which carries the "Default - " label prefix.
const dedupeDevicesByGroupId = (
    devices: MediaDevice[] | null,
): MediaDevice[] | null => {
    if (!devices) {
        return devices;
    }
    const VIRTUAL_IDS = new Set(['default', 'communications']);
    const virtualGroupIds = new Set(
        devices
            .filter((d) => VIRTUAL_IDS.has(d.deviceId))
            .map((d) => d.groupId),
    );
    return devices.filter(
        (d) => VIRTUAL_IDS.has(d.deviceId) || !virtualGroupIds.has(d.groupId),
    );
};

interface MeetingDeviceSettingsProps {
    meetingLocation: keyof typeof MeetingLocation;
    /**
     * Render with the dark palette — for the always-dark in-meeting settings
     * popover. The lobby leaves it off and stays light / theme-following.
     */
    dark?: boolean;
}

interface ConditionalWrapperProps {
    condition: boolean;
    disabledToolTip: string | null;
    /** The disabled select points here with aria-describedby. */
    tooltipId: string;
    children: React.ReactNode;
}

const ConditionalWrapper: React.FC<ConditionalWrapperProps> = ({
    condition,
    disabledToolTip,
    tooltipId,
    children,
}) => {
    if (!condition || !disabledToolTip) {
        return <>{children}</>;
    }

    return (
        <div title={disabledToolTip} className="group relative">
            {children}
            <div
                id={tooltipId}
                role="tooltip"
                className="absolute start-1/2 bottom-full z-10 mb-2 -translate-x-1/2 transform rounded bg-gray-800 px-3 py-2 text-sm whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            >
                {disabledToolTip}
                <div className="absolute start-1/2 top-full -translate-x-1/2 transform border-4 border-transparent border-t-gray-800" />
            </div>
        </div>
    );
};

const MeetingDeviceSettings: React.FC<MeetingDeviceSettingsProps> = ({
    meetingLocation,
    dark = false,
}) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const idPrefix = useId();

    const micInUse = useAppSelector(selectMicInUse);
    const audioOutputInUse = useAppSelector(selectAudioOutputInUse);
    const videoInUse = useAppSelector(selectVideoInUse);
    const micList = dedupeDevicesByGroupId(useAppSelector(selectMicDeviceList));
    const audioOutputList = dedupeDevicesByGroupId(
        useAppSelector(selectAudioOutputDeviceList),
    );
    const videoList = dedupeDevicesByGroupId(
        useAppSelector(selectVideoDeviceList),
    );
    const lobbyMicInUse = useAppSelector(selectLobbyMicDeviceInUse);
    const lobbyVideoInUse = useAppSelector(selectLobbyVideoDeviceInUse);
    const lobbyAudioOutputInUse = useAppSelector(selectLobbyAudioDeviceInUse);

    const isInLobby = meetingLocation === MeetingLocation.LOBBY;

    const micDeviceInUse = isInLobby ? lobbyMicInUse : micInUse;
    const audioDeviceInUse = isInLobby
        ? lobbyAudioOutputInUse
        : audioOutputInUse;
    const videoDeviceInUse = isInLobby ? lobbyVideoInUse : videoInUse;

    // setSinkId routing is unreliable across platforms for WebRTC streams, so
    // restrict the speaker picker to the OS default. Falls back to the full
    // list if the 'default' entry isn't enumerated for some reason.
    const audioOutputDefaultOnly = audioOutputList?.filter(
        (device: MediaDevice) => device.deviceId === 'default',
    );
    const speakerList =
        audioOutputDefaultOnly && audioOutputDefaultOnly.length > 0
            ? audioOutputDefaultOnly
            : audioOutputList;
    const speakerInUse =
        speakerList === audioOutputDefaultOnly
            ? audioOutputDefaultOnly[0]
            : audioDeviceInUse;

    const changeMicrophone = (e: ChangeEvent<HTMLSelectElement>) => {
        dispatch(changeMicDevice(e.target.value, meetingLocation));
    };

    const changeVideo = (e: ChangeEvent<HTMLSelectElement>) => {
        dispatch(changeVideoDevice(e.target.value, meetingLocation));
    };

    const deviceSelector = (
        title: string,
        name: string,
        deviceInUse: MediaDevice | null,
        deviceList: MediaDevice[] | null,
        onChange: (e: ChangeEvent<HTMLSelectElement>) => void,
        disabled: boolean,
        disabledToolTip: string | null,
    ) => {
        const selectId = `${idPrefix}-${name}`;
        const tooltipId = `${selectId}-disabled-reason`;
        const content = (
            <div>
                <label
                    htmlFor={selectId}
                    className={twMerge(
                        'mb-1 block text-sm',
                        dark ? 'text-gray-400' : 'text-gray-600',
                    )}
                >
                    {i18next.t(title)}
                </label>
                <div>
                    <select
                        id={selectId}
                        // A disabled select cannot take focus, but a screen
                        // reader browsing the dialog still reads the reason
                        // with it instead of finding the tooltip on its own.
                        aria-describedby={
                            disabled && disabledToolTip ? tooltipId : undefined
                        }
                        className={twMerge(
                            'mb-5 w-full appearance-none px-4 py-3 pe-10',
                            'bg-[length:1.5em_1.5em] bg-[right_0.75rem_center] bg-no-repeat rtl:bg-[left_0.75rem_center]',
                            'focus-ring',
                            dark
                                ? 'bg-[url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%23d1d5db%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27m6 8 4 4 4-4%27/%3e%3c/svg%3e")] rounded-lg bg-gray-700 text-white'
                                : 'bg-[url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27m6 8 4 4 4-4%27/%3e%3c/svg%3e")] rounded border border-gray-300 bg-white',
                            disabled && 'cursor-not-allowed opacity-50',
                        )}
                        name={name}
                        value={deviceInUse?.deviceId || ''}
                        onChange={onChange}
                        disabled={disabled}
                    >
                        {deviceList?.map((option) => (
                            <option
                                value={option.deviceId}
                                key={option.deviceId}
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );

        return (
            <ConditionalWrapper
                condition={disabled}
                disabledToolTip={disabledToolTip}
                tooltipId={tooltipId}
            >
                {content}
            </ConditionalWrapper>
        );
    };

    return (
        <div className="flex h-full w-full flex-col">
            {deviceSelector(
                'media.settings.microphone',
                'microphone',
                micDeviceInUse,
                micList,
                changeMicrophone,
                isFirefox(),
                i18next.t(
                    'videoconference.lobby.device_settings.browser_change_text',
                    { browser: 'Firefox' },
                ),
            )}
            {deviceSelector(
                'media.settings.speaker',
                'audio',
                speakerInUse,
                speakerList,
                () => {},
                false,
                null,
            )}
            {deviceSelector(
                'media.settings.camera',
                'video',
                videoDeviceInUse,
                videoList,
                changeVideo,
                false,
                null,
            )}
        </div>
    );
};

export default MeetingDeviceSettings;
